import { Button } from "@msuite/ui/button";
import { Select } from "@msuite/ui/form";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import type { RoleMember } from "@msuite/utils/types";
import { getFullName } from "@msuite/utils/users";
import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { client } from "@/hono";
import { useMembers } from "@/hooks/use-members";

/** Props Interface */
interface RoleMemberSelectProps {
	roleId: string;
	members: RoleMember[];
}

export function RoleMemberSelect({ roleId, members }: RoleMemberSelectProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const qc = useQueryClient();

	/** State */
	const [isAdding, setIsAdding] = useState(false);

	/** Form */
	// One person at a time: the select holds the pick, the button commits it. Deliberately not
	// `multiple` — adding a second person is picking again, not building a list first. And the
	// button is what commits, rather than the select's `onChange`, because the write goes out the
	// moment it is called and nothing here can take it back: a role hands out permissions, so
	// picking the wrong name from a list of sixty must stay a pick and not already be a grant.
	const methods = useForm<{ userId: string | null }>({ defaultValues: { userId: null } });
	const selectedUserId = useWatch({ control: methods.control, name: "userId" });

	/** Query */
	// The organisation's members, through the shared hook whose key carries the organisation: a
	// bare ["members"] key would write into the cache every other members list reads — including
	// a `null` resolved before the active organisation is known.
	const { members: organizationMembers, isPending } = useMembers();

	/** Functions */
	async function handleOnAddMember() {
		const userId = methods.getValues("userId");
		if (!userId) return;
		try {
			setIsAdding(true);
			const response = await client.roles[":roleId"].members[":userId"].$put({
				param: { roleId, userId },
			});
			// The toast is raised here rather than by the handler, so that every failure — a coded
			// refusal, a 500, a dead network — leaves exactly one message on screen instead of
			// none.
			await responseHandler(response, false);
			toast.success(t("roles.member_added"));
			methods.reset({ userId: null });
			// The same invalidation the user page raises for the same write — see the note in
			// `role-members.tsx`. It must reach all three readers: the roles list's member counts,
			// the adder's own abilities (an admin can add themselves), and the user page's grid.
			await qc.invalidateQueries({
				predicate: invalidate(["roles", "citadel", "user-permissions-form"]),
			});
		} catch (error) {
			toast.error(error instanceof Error ? error.message : t("general.something_went_wrong"));
		} finally {
			setIsAdding(false);
		}
	}

	/** Computed */
	const assignedUserIds = new Set(members.map((member) => member.id));
	// The owner is deliberately absent: the resolver answers for them before it reads a role, so
	// handing them one changes nothing and the server refuses it anyway. So is anybody whose
	// membership is not `active` — an offboarded or never-accepted person cannot use what the role
	// grants, and offering them turns the picker into a list of people nobody meant to see.
	const options = organizationMembers
		.filter((member) => member.membership.state === "active")
		.filter((member) => member.membership.role !== "owner" && !assignedUserIds.has(member.user.id))
		.map((member) => ({
			value: member.user.id,
			label: getFullName(member.user),
			item: null,
		}));

	/** Guard */
	// Nobody left to add is a sentence, not a disabled control that opens onto nothing. Held
	// back until the members query has answered, or the page would announce it while the list
	// is still on the wire.
	if (!isPending && !options.length) {
		return (
			<p
				className="label"
				data-cy="role-member-select-empty"
			>
				{t("roles.no_members_left")}
			</p>
		);
	}

	/** Render */
	// The select is boxed to a fixed width and the row wraps. Both matter: the select's own
	// wrapper is `w-full`, so an unboxed select grows until the add button sits past the right
	// edge of a card that is already wider than the window — reachable only by sideways
	// scrolling, which reads as a button that does nothing.
	//
	// No label: the control sits beside the table's search field, and a label above it pushed
	// the row out of line. The placeholder carries the noun instead, and the button beside it
	// says what committing the pick does.
	return (
		<div className="flex-wrap items-center gap-2 h-stack">
			<div className="w-[260px] max-w-full">
				<Select
					control={methods.control}
					name="userId"
					label={undefined}
					placeholder={t("placeholders.select_member")}
					isRequired={false}
					isNullable={false}
					isDisabled={!options.length}
					data-cy="role-member-select"
					options={options}
				/>
			</div>
			<Button
				variant="primary"
				size="sm"
				icon={<Icon name="Plus" />}
				isDisabled={!selectedUserId}
				isLoading={isAdding}
				onClick={handleOnAddMember}
				data-cy="role-member-add-button"
			>
				{t("general.add")}
			</Button>
		</div>
	);
}
